import { useSyncExternalStore } from 'react';
import type { EnrichedLead } from '@repo/contact-form-backend/src/lib/enrichment/enrichLead';

/**
 * In-memory + sessionStorage atom for the homepage → /contact funnel.
 * Email stays off the URL (PII in query strings leaks via logs and referrers).
 * sessionStorage keeps a same-tab refresh working; a new tab or shared link
 * starts with an empty email and a new submission.
 */
const STORAGE_KEY = 'transcend.pendingContactSubmission';

export type PendingContactSubmission = {
  email: string;
  submissionId?: string;
  enriched?: EnrichedLead | null;
};

let memory: PendingContactSubmission | null = null;
const listeners = new Set<() => void>();

function readFromSession(): PendingContactSubmission | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingContactSubmission;
    if (typeof parsed?.email !== 'string' || parsed.email.length === 0) {
      return null;
    }
    if (
      parsed.submissionId !== undefined &&
      typeof parsed.submissionId !== 'string'
    ) {
      return null;
    }
    return {
      email: parsed.email,
      submissionId: parsed.submissionId,
      enriched: parsed.enriched ?? null,
    };
  } catch {
    return null;
  }
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function getPendingContactSubmission(): PendingContactSubmission | null {
  if (memory) {
    return memory;
  }
  memory = readFromSession();
  return memory;
}

export function setPendingContactSubmission(
  value: PendingContactSubmission,
): void {
  memory = value;
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  }
  emit();
}

export function clearPendingContactSubmission(): void {
  memory = null;
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
  emit();
}

export function usePendingContactSubmission(): PendingContactSubmission | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getPendingContactSubmission,
    () => null,
  );
}
